import { Fragment } from "react";
import styled, { css } from "styled-components";

import { faArrowsRotate } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";

import { BackInTimeWarning } from "pages/Sikring/opprett/BackInTimeWarning";
import { Grid, Hint, Input, Label, StepHeading, StepWrapper } from "pages/Sikring/opprett/CreateSikringPage";
import { HighVolumeWarning } from "pages/Sikring/opprett/HighVolumeWarning";
import { OverlapWarning, canOverrideOverlap } from "pages/Sikring/opprett/OverlapWarning";
import { ENERGI_MAIL_LOCKED, WizardState } from "pages/Sikring/opprett/wizardTypes";
import {
	Month,
	PRICE_UNIT,
	TotalDistribution,
	VolumeAndPrice,
	formatNumber,
	fmtPercent,
	fmtPriceWithUnit,
	isDefined,
	marginError,
	maxEndMonth,
	maxSikringMonths,
	overlapKey,
	pasteMonthColumn,
	INVALID_NUMBER_MESSAGE,
	backInTimeError,
	earliestStartMonth,
	hourShares,
	exceedsPriceDigits,
	isInvalidNumber,
	periodError,
	standardProfileShares,
} from "pages/Sikring/sikringUtils";
import { SikringMaalepunkt, SikringOmfangType, Sikringsordre } from "pages/Sikring/types";

interface Step3Props {
	/** Nummeret steget har i veiviseren. Kontraktsteget foran er ikke alltid med. */
	stepNumber: number;
	wiz: WizardState;
	volumeAndPrice: VolumeAndPrice;
	/** Eksisterende sikringer på samme omfang som overlapper perioden. */
	overlaps: Sikringsordre[];
	/** Satt mens eksisterende sikringer hentes. Steget er ugyldig så lenge, siden overlapp ikke er sjekket ennå. */
	overlapCheckPending: boolean;
	/** Satt når eksisterende sikringer ikke kunne hentes, så overlapp ikke er sjekket. */
	overlapCheckFailed: boolean;
	/** Henter sikringsordrene på nytt. Brukes til å prøve igjen etter en feilet overlappsjekk. */
	onRetryOverlapCheck: () => void;
	/** Målepunktene en sikring på kundenivå omfatter, for å sammenligne volumet med forventet forbruk. Tom ellers. */
	customerMeteringPoints: SikringMaalepunkt[];
	update: <K extends keyof WizardState>(field: K, value: WizardState[K]) => void;
}

/**
 * Gyldig periode, volum for hele perioden, en kundepris som ikke gir negativ sikringsmargin og overlapp sjekket
 * og eventuelt bekreftet. Steget er ugyldig mens eksisterende sikringer hentes, så brukeren kommer ikke
 * videre før overlappen er sjekket - og det samme gjelder om hentingen feilet: en feil gir tom
 * overlaps-liste (fallback i CreateSikringPage), som ellers hadde sett ut som «ingen overlapp».
 */
export function getMissingFieldsForStep3(
	wiz: WizardState,
	volumeAndPrice: VolumeAndPrice,
	overlaps: Sikringsordre[],
	overlapCheckPending: boolean,
	overlapCheckFailed: boolean
): string[] {
	const missing: string[] = [];
	// countMonths gir 0 for en tom eller ugyldig måned og 0 eller mindre når slutt er før start.
	if (periodError(wiz.startMonth, wiz.endMonth, wiz.skipContract) !== null || volumeAndPrice.months.length === 0) missing.push("Velg en gyldig periode.");
	else if (backInTimeError(wiz.startMonth, wiz.skipContract) !== null) missing.push("Velg en startmåned som ikke er tilbake i tid.");
	if (overlapCheckPending) missing.push("Sjekker overlapp mot eksisterende sikringer.");
	else if (overlapCheckFailed) missing.push("Klarte ikke å sjekke overlapp mot eksisterende sikringer.");
	// Bekreftelsen gjelder akkurat de overlappende sikringene, så den faller bort når overlappen endres.
	// I produksjon kan overlapp ikke bekreftes, bare i dev og lokalt.
	else if (overlaps.length > 0 && !(canOverrideOverlap() && wiz.overlapAcknowledgedFor === overlapKey(overlaps)))
		missing.push("Perioden overlapper med en eksisterende sikring.");
	// Når månedsvolumer sendes (lagt inn per måned eller standard fordeling), krever backend en verdi for hver måned i perioden.
	const allMonthsFilled = !volumeAndPrice.monthlyVolumes || Object.values(volumeAndPrice.monthlyVolumes).every((v) => v > 0);
	if ((volumeAndPrice.volumKwh ?? 0) <= 0) missing.push("Fyll ut volum.");
	else if (!allMonthsFilled)
		// Standard fordeling runder ned per måned, så et lite totalvolum kan gi måneder med 0.
		missing.push(wiz.volumeMode === "monthly" ? "Fyll ut volum for alle månedene." : "Volumet er for lite til å fordeles på alle månedene.");
	if ((volumeAndPrice.innpris ?? 0) <= 0) missing.push("Fyll ut kjøpspris.");
	if (!isDefined(volumeAndPrice.fortjeneste)) missing.push("Fyll ut kundepris.");
	else if (marginError(volumeAndPrice.fortjeneste) !== null) missing.push("Kundeprisen må være minst like høy som kjøpsprisen.");
	return missing;
}

export function Step3PeriodVolumePrice({
	stepNumber,
	wiz,
	volumeAndPrice,
	overlaps,
	overlapCheckPending,
	overlapCheckFailed,
	onRetryOverlapCheck,
	customerMeteringPoints,
	update,
}: Readonly<Step3Props>) {
	const { months, volumKwh, monthlyVolumes, fortjeneste } = volumeAndPrice;
	const isStandard = wiz.totalDistribution === TotalDistribution.Standard;
	const shares = isStandard ? standardProfileShares(months) : hourShares(months);
	// Fordelingen runder ned til hele kWh, så et svært lite totalvolum kan gi måneder med 0 - da er steget ugyldig.
	const hasEmptyMonth = wiz.volumeMode === "total" && !!monthlyVolumes && Object.values(monthlyVolumes).some((v) => v <= 0);
	const periodErrorMessage = periodError(wiz.startMonth, wiz.endMonth, wiz.skipContract);
	const backInTimeErrorMessage = backInTimeError(wiz.startMonth, wiz.skipContract);
	const marginErrorMessage = marginError(fortjeneste);
	// Et ugyldig månedsvolum telles som 0 i summen, så det må vises hvilke måneder det gjelder.
	const invalidMonths = wiz.volumeMode === "monthly" ? months.filter((m) => isInvalidNumber(wiz.volumePerMonth[m.key] ?? "")) : [];

	return (
		<StepWrapper $gap="22px">
			<StepHeading>Steg {stepNumber} – Periode, volum og pris</StepHeading>
			<Grid>
				<div>
					<Label>Fra og med måned</Label>
					<Input
						type="month"
						min={wiz.skipContract ? undefined : earliestStartMonth()}
						value={wiz.startMonth}
						onChange={(e) => update("startMonth", e.target.value)}
						autoComplete="off"
					/>
					{backInTimeErrorMessage && <ErrorText>{backInTimeErrorMessage}</ErrorText>}
				</div>
				<div>
					<Label>Til og med måned</Label>
					<Input
						type="month"
						min={wiz.startMonth || undefined}
						max={maxEndMonth(wiz.startMonth, wiz.skipContract) || undefined}
						value={wiz.endMonth}
						onChange={(e) => update("endMonth", e.target.value)}
						autoComplete="off"
					/>
					{periodErrorMessage && <ErrorText>{periodErrorMessage}</ErrorText>}
					<Hint>Perioden må være fra én måned til {maxSikringMonths(wiz.skipContract) / 12} år.</Hint>
				</div>
				{overlapCheckPending && (
					<div style={{ gridColumn: "1 / -1" }}>
						<Hint style={{ margin: 0 }}>Sjekker om perioden overlapper eksisterende sikringer …</Hint>
					</div>
				)}
				{!overlapCheckPending && (overlaps.length > 0 || overlapCheckFailed) && (
					<div style={{ gridColumn: "1 / -1" }}>
						<OverlapWarning
							overlaps={overlaps}
							acknowledgedFor={wiz.overlapAcknowledgedFor}
							onAcknowledge={(key) => update("overlapAcknowledgedFor", key)}
						/>
						{overlapCheckFailed && (
							<>
								<ErrorText>Kunne ikke hente eksisterende sikringer, så overlapp med andre sikringer er ikke sjekket.</ErrorText>
								<Button
									secondary
									type="button"
									icon={<FontAwesomeIcon icon={faArrowsRotate} />}
									label="Prøv igjen"
									disabled={overlapCheckPending}
									onClick={onRetryOverlapCheck}
									style={{ marginTop: "8px" }}
								/>
							</>
						)}
					</div>
				)}
				{/* Med kontrakt er tilbake i tid en feil ved startmåneden, ikke et varsel. */}
				{wiz.skipContract && (
					<div style={{ gridColumn: "1 / -1" }}>
						<BackInTimeWarning startMonth={wiz.startMonth} />
					</div>
				)}

				<div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "12px" }}>
					<Label as="span">Volum</Label>
					<RadioRow>
						<RadioOption>
							<input type="radio" checked={wiz.volumeMode === "total"} onChange={() => update("volumeMode", "total")} />
							Totalvolum
						</RadioOption>
						<RadioOption>
							<input type="radio" checked={wiz.volumeMode === "monthly"} onChange={() => update("volumeMode", "monthly")} />
							Legg inn per måned
						</RadioOption>
					</RadioRow>
					{wiz.volumeMode === "total" && (
						<TotalVolumeGroup>
							<div>
								<SubLabel htmlFor="totalVolume">Totalvolum for perioden</SubLabel>
								<FieldWithUnit style={{ maxWidth: "280px" }}>
									<Input
										id="totalVolume"
										type="text"
										placeholder="f.eks. 45 000"
										value={wiz.totalVolume}
										onChange={(e) => update("totalVolume", e.target.value)}
										autoComplete="off"
									/>
									<SmallText>kWh</SmallText>
								</FieldWithUnit>
							</div>
							<div>
								<SubLabel as="span">Fordeling over månedene</SubLabel>
								<RadioRow>
									<RadioOption>
										<input
											type="radio"
											checked={wiz.totalDistribution === TotalDistribution.Standard}
											onChange={() => update("totalDistribution", TotalDistribution.Standard)}
										/>
										Standard fordeling
									</RadioOption>
									<RadioOption>
										<input
											type="radio"
											checked={wiz.totalDistribution === TotalDistribution.Flat}
											onChange={() => update("totalDistribution", TotalDistribution.Flat)}
										/>
										Flat fordeling
									</RadioOption>
								</RadioRow>
								<Hint>
									{wiz.totalDistribution === TotalDistribution.Standard
										? "Følger en fast månedsprofil med mer volum om vinteren."
										: "Samme volum hver time, så månedene får volum etter antall timer."}
								</Hint>
							</div>
						</TotalVolumeGroup>
					)}
					{/*
					 * min/max på feltene hindrer ikke en for lang periode (f.eks. når startmåneden flyttes etter at sluttmåneden er valgt).
					 * Tabellen skjules i stedet dersom brukeren klarer å velge en for lang periode
					 */}
					{months.length > 0 && !periodErrorMessage && (
						<MonthBox>
							<MonthBoxTitle>{monthBoxTitle(wiz)}</MonthBoxTitle>
							{wiz.volumeMode === "total" ? (
								<>
									<DistributionTable months={months} volumes={monthlyVolumes} shares={shares} />
									{hasEmptyMonth && (
										<ErrorText>
											Volumet er for lite til å fordeles på alle månedene – minst én måned blir 0 kWh.{" "}
											{isStandard ? "Øk volumet eller velg flat fordeling." : "Øk volumet."}
										</ErrorText>
									)}
									<Hint>
										{isStandard
											? "Prosenten er månedens andel av totalvolumet. For et helt kalenderår er det standardprofilen; for kortere perioder er andelene skalert så de summerer til 100 %. Volumene er rundet til hele kWh og summerer seg alltid til totalvolumet."
											: "Prosenten er månedens andel av timene i perioden, med sommertid tatt med. Volumene er rundet ned til hele kWh, og resten legges på februar – eller siste måned når perioden ikke har februar."}
									</Hint>
								</>
							) : (
								<>
									<MonthGrid>
										{months.map((m) => (
											<MonthRow key={m.key}>
												<SmallText>{m.label}</SmallText>
												<MonthInput
													type="text"
													value={wiz.volumePerMonth[m.key] ?? ""}
													aria-invalid={isInvalidNumber(wiz.volumePerMonth[m.key] ?? "")}
													onChange={(e) => update("volumePerMonth", { ...wiz.volumePerMonth, [m.key]: e.target.value })}
													onPaste={(e) => {
														const pasted = pasteMonthColumn(e.clipboardData.getData("text/plain"), months, m.key);
														if (!pasted) return;
														e.preventDefault();
														update("volumePerMonth", { ...wiz.volumePerMonth, ...pasted });
													}}
													autoComplete="off"
												/>
											</MonthRow>
										))}
									</MonthGrid>
									{invalidMonths.length > 0 && (
										<ErrorText>
											{INVALID_NUMBER_MESSAGE} Gjelder {invalidMonths.map((m) => m.label).join(", ")}.
										</ErrorText>
									)}
									<Hint>
										Backend krever en verdi for hver måned i perioden når volumet legges inn per måned. Du kan lime inn en kolonne fra Excel
										– den fylles inn fra og med feltet du limer inn i.
									</Hint>
								</>
							)}
							<MonthBoxSum>Sum: {formatNumber(volumKwh ?? 0)} kWh</MonthBoxSum>
						</MonthBox>
					)}
				</div>

				{wiz.scope === SikringOmfangType.Kunde && (
					<div style={{ gridColumn: "1 / -1" }}>
						<HighVolumeWarning volumKwh={volumKwh} months={months} meteringPoints={customerMeteringPoints} />
					</div>
				)}

				<div>
					<Label>Kjøpspris (eks. mva)</Label>
					<FieldWithUnit>
						<Input
							type="text"
							placeholder="f.eks. 57,00"
							value={wiz.innpris}
							onChange={(e) => !exceedsPriceDigits(e.target.value) && update("innpris", e.target.value)}
							autoComplete="off"
						/>
						<SmallText>{PRICE_UNIT}</SmallText>
					</FieldWithUnit>
					<Hint>Bruk komma som desimalskilletegn.</Hint>
					{isInvalidNumber(wiz.innpris) && <ErrorText>{INVALID_NUMBER_MESSAGE}</ErrorText>}
				</div>
				<div>
					<Label>Kundepris (eks. mva)</Label>
					<FieldWithUnit>
						<Input
							type="text"
							placeholder="f.eks. 62,50"
							value={wiz.kundepris}
							onChange={(e) => !exceedsPriceDigits(e.target.value) && update("kundepris", e.target.value)}
							autoComplete="off"
						/>
						<SmallText>{PRICE_UNIT}</SmallText>
					</FieldWithUnit>
					<Hint>Bruk komma som desimalskilletegn.</Hint>
					<CalculatedValue>
						Sikringsmargin: <strong>{fmtPriceWithUnit(fortjeneste)}</strong>
					</CalculatedValue>
					{isInvalidNumber(wiz.kundepris) && <ErrorText>{INVALID_NUMBER_MESSAGE}</ErrorText>}
					{marginErrorMessage && <ErrorText>{marginErrorMessage}</ErrorText>}
				</div>

				<div style={{ gridColumn: "1 / -1" }}>
					<Label as="span">Innkjøp</Label>
					<CheckboxOption>
						<input
							type="checkbox"
							checked={!wiz.skipEnergiMail}
							disabled={ENERGI_MAIL_LOCKED}
							onChange={(e) => update("skipEnergiMail", !e.target.checked)}
						/>
						Send innkjøpsbestilling til NTE Energi
						{ENERGI_MAIL_LOCKED && <SmallText>(ikke tilgjengelig ennå)</SmallText>}
					</CheckboxOption>
					<Hint>Når sikringen er sendt til UtilityCloud, får NTE Energi normalt en e-post om å kjøpe inn volumet.</Hint>
				</div>
			</Grid>
		</StepWrapper>
	);
}

function monthBoxTitle(wiz: WizardState): string {
	if (wiz.volumeMode === "monthly") return "Volum per måned (kWh)";
	return wiz.totalDistribution === TotalDistribution.Standard
		? "Beregnet fordeling per måned (kWh) – standard fordeling"
		: "Beregnet fordeling per måned (kWh) – flat fordeling";
}

const ErrorText = styled.p`
	font-size: 0.75rem;
	color: ${colors.coral};
	margin: 8px 0 0;
`;

const RadioRow = styled.div`
	display: flex;
	gap: 28px;
	flex-wrap: wrap;
`;

const TotalVolumeGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-left: 6px;
	padding: 4px 0 4px 18px;
	box-shadow: inset 2px 0 0 ${colors.grey300};
`;

const SubLabel = styled.label`
	display: block;
	font-weight: 700;
	font-size: 0.8125rem;
	color: ${colors.grey800};
	margin-bottom: 8px;
`;

const RadioOption = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const CheckboxOption = styled(RadioOption)`
	input[type="checkbox"] {
		margin: 0;
	}
`;

const FieldWithUnit = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const CalculatedValue = styled.p`
	margin: 10px 0 0;
	font-size: 1rem;
	color: ${colors.grey900};
`;

const SmallText = styled.span`
	font-size: 0.8125rem;
	color: ${colors.grey800};
	white-space: nowrap;
`;

const MonthBox = styled.div`
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
	padding: 16px 18px;
`;

const MonthBoxTitle = styled.div`
	font-weight: 700;
	font-size: 0.8125rem;
	color: ${colors.grey800};
	margin-bottom: 12px;
`;

const MonthBoxSum = styled.div`
	margin-top: 14px;
	font-weight: 700;
	font-size: 0.8125rem;
`;

const MonthGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 10px 20px;
`;

const TABLE_GROUPS = 3;

interface DistributionTableProps {
	months: Month[];
	/** Null til totalvolumet er fylt ut - da vises bare andelene. */
	volumes: Record<string, number> | null;
	shares: Record<string, number>;
}

function DistributionTable({ months, volumes, shares }: Readonly<DistributionTableProps>) {
	const rowCount = Math.ceil(months.length / TABLE_GROUPS);
	const groups = Array.from({ length: Math.ceil(months.length / rowCount) }, (_, g) => months.slice(g * rowCount, (g + 1) * rowCount));

	return (
		<DistributionGrid $groups={groups.length}>
			{groups.map((_, g) => (
				<Fragment key={`header-${g}`}>
					<HeaderCell $groupStart={g > 0}>Måned</HeaderCell>
					<HeaderCell $right>kWh</HeaderCell>
					<HeaderCell $right>Andel</HeaderCell>
				</Fragment>
			))}
			{Array.from({ length: rowCount }, (_, r) =>
				groups.map((group, g) => {
					const month = group[r];
					return (
						<Fragment key={`${r}-${g}`}>
							<Cell $groupStart={g > 0}>{month?.label}</Cell>
							<Cell $right $strong>
								{month && (volumes ? formatNumber(volumes[month.key]) : "–")}
							</Cell>
							<Cell $right $muted>
								{month && fmtPercent(shares[month.key])}
							</Cell>
						</Fragment>
					);
				})
			)}
		</DistributionGrid>
	);
}

const DistributionGrid = styled.div<{ $groups: number }>`
	display: grid;
	grid-template-columns: repeat(${(props) => props.$groups}, auto minmax(56px, 1fr) auto);
	column-gap: 12px;
	font-size: 0.8125rem;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`;

interface CellProps {
	$groupStart?: boolean;
	$right?: boolean;
	$strong?: boolean;
	$muted?: boolean;
}

const Cell = styled.div<CellProps>`
	padding: 5px 0;
	text-align: ${(props) => (props.$right ? "right" : "left")};
	font-weight: ${(props) => (props.$strong ? 700 : 400)};
	color: ${(props) => (props.$muted ? colors.grey700 : colors.grey900)};
	${(props) =>
		props.$groupStart &&
		css`
			padding-left: 16px;
			box-shadow: inset 1px 0 0 ${colors.grey300};
		`}
`;

const HeaderCell = styled(Cell)`
	font-weight: 700;
	color: ${colors.grey800};
	border-bottom: 1px solid ${colors.grey300};
	margin-bottom: 4px;
`;

const MonthRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

// Kompakt felt i månedsrutenettet: padding og fontstørrelse må overstyres sammen med høyden,
// ellers klipper den globale paddingen på 15px teksten.
const MonthInput = styled.input`
	width: 86px;
	height: 34px;
	padding: 6px 8px;
	font-size: 0.875rem;
	text-align: right;
	box-sizing: border-box;

	&[aria-invalid="true"] {
		border-color: ${colors.coral};
	}
`;
